import React from 'react';
import AnimatedSection from '../../common/AnimatedSection';

export default function TeamSection({ teamData = [] }) {
  const getLocalPhotoPath = (role) => {
    switch (role) {
      case 'Secrétaire Général':
        return '/bureau_acem_fes/Secrétaire général/image.jpeg';
      case 'Chargé des Affaires Estudiantines et Socioculturelles':
        return '/bureau_acem_fes/Chargé académique et culturel/image.jpeg';
      case 'Chargé Sportif':
        return '/bureau_acem_fes/Chargé sportif/image.jpeg';
      case 'Trésorier Général':
        return '/bureau_acem_fes/Tresorier général/image.jpeg';
      case 'Chargé de la Communication':
        return '/bureau_acem_fes/Chargé de la communication/image.jpeg';
      case 'Contrôleur Général':
        return '/bureau_acem_fes/Controleur/image.jpeg';
      default:
        return null;
    }
  };

  const getObjectPosition = (role) => {
    if (role === 'Trésorier Général') return 'center 15%';
    if (role === 'Chargé des Affaires Estudiantines et Socioculturelles') return 'center 15%';
    if (role === 'Chargé Sportif') return 'center top';
    if (role === 'Chargé de la Communication') return 'center 45%';
    if (role === 'Secrétaire Général') return 'center 5%';
    return 'center';
  };

  return (
    <AnimatedSection animation="stagger" selector=".team-card" stagger={0.1} className="max-w-5xl mx-auto my-12 px-4">
      <div className="text-center mb-8">
        <h2 className="font-title text-2xl sm:text-3xl font-extrabold text-acem-primary-dark mb-2">
          Bureau ACEM Fès — Mandat 2025/2026
        </h2>
        <p className="text-sm text-acem-muted">
          Une équipe engagée au service de la communauté estudiantine comorienne.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6">
        {teamData.map((member, index) => {
          const localPath = getLocalPhotoPath(member.role);
          const initialSrc = localPath || member.photoUrl;

          return (
            <div
              key={index}
              className="team-card group relative aspect-[3/4] min-h-[320px] max-w-[310px] w-full mx-auto rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-black/10 bg-acem-surface-container-high flex flex-col justify-end"
            >
              {/* Photo en pleine surface */}
              {initialSrc ? (
                <img
                  src={initialSrc}
                  alt={member.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                  style={{ objectPosition: getObjectPosition(member.role) }}
                  loading="lazy"
                  onError={(e) => {
                    if (member.photoUrl && e.currentTarget.src !== member.photoUrl) {
                      e.currentTarget.src = member.photoUrl;
                    }
                  }}
                />
              ) : (
                <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-acem-surface-container-high text-acem-primary/40">
                  <svg className="w-16 h-16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
              )}

              {/* Overlay dégradé noir -> transparent */}
              <div
                className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/55 via-50% to-black/15 group-hover:via-black/65 transition-colors duration-300 pointer-events-none"
                aria-hidden="true"
              />

              {/* Badge Rôle en haut */}
              <div className="absolute top-3 left-3 right-3 z-10 flex items-center justify-between">
                <span className="text-[9px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full bg-black/45 backdrop-blur-md text-white/90 border border-white/20 shadow-sm truncate max-w-[85%]">
                  {member.role}
                </span>
                <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)] flex-shrink-0" title="Actif - Mandat 2025/2026" />
              </div>

              {/* Contenu superposé en bas */}
              <div className="relative z-10 p-4 sm:p-5 space-y-1.5">
                <div>
                  <h3 className="font-title text-base sm:text-lg font-bold text-white leading-tight drop-shadow-md group-hover:text-amber-200 transition-colors">
                    {member.name}
                  </h3>
                  <p className="text-[11px] sm:text-xs font-semibold text-white/80 line-clamp-1 drop-shadow-sm mt-0.5">
                    {member.role}
                  </p>
                </div>

                {/* Actions Contacts */}
                <div className="flex items-center gap-2 pt-2 border-t border-white/20">
                  {member.whatsapp && (
                    <a
                      href={member.whatsapp}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 py-1.5 px-2.5 rounded-xl bg-[#25D366] hover:bg-[#20ba59] text-white text-[11px] font-bold shadow-sm inline-flex items-center justify-center gap-1.5 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <svg className="w-3 h-3 fill-current flex-shrink-0" viewBox="0 0 24 24">
                        <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.196 8.196 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24m4.52 11.66c-.25-.13-1.47-.72-1.7-.81-.23-.08-.39-.13-.56.13-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.13-1.06-.39-2.01-1.24-.74-.66-1.24-1.48-1.39-1.73-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.44-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1s.9 2.44 1.03 2.61c.13.17 1.77 2.7 4.29 3.79.6.26 1.07.41 1.44.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.11-.22-.18-.47-.3z" />
                      </svg>
                      <span>WhatsApp</span>
                    </a>
                  )}
                  {member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      onClick={(e) => {
                        if (!/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                          e.preventDefault();
                          window.open(`https://accounts.google.com/AccountChooser?continue=https%3A%2F%2Fmail.google.com%2Fmail%2F%3Fview%3Dcm%26fs%3D1%26to%3D${member.email}`, '_blank');
                        }
                      }}
                      className="flex-1 py-1.5 px-2.5 rounded-xl bg-white/15 hover:bg-white/25 text-white backdrop-blur-md border border-white/25 text-[11px] font-bold shadow-sm inline-flex items-center justify-center gap-1.5 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>Email</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </AnimatedSection>
  );
}

